
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Settings, LogOut, Bell, Search } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface UserMenuProps {
  userImageUrl?: string;
  userFirstName?: string;
  userLastName?: string;
  fullName?: string;
  onLogout: () => void;
}

const UserMenu: React.FC<UserMenuProps> = ({ 
  userImageUrl, 
  userFirstName, 
  userLastName,
  fullName,
  onLogout
}) => {
  const navigate = useNavigate();
  
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div className="relative group cursor-pointer">
          <Avatar className="h-9 w-9 border-2 border-white/20 group-hover:border-neon-purple/50 transition-all duration-300">
            <AvatarImage src={userImageUrl || ""} />
            <AvatarFallback className="text-xs bg-black/50 backdrop-blur-sm text-white">
              {userFirstName?.charAt(0) || "U"}
              {userLastName?.charAt(0) || ""}
            </AvatarFallback>
          </Avatar>
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-neon-blue via-neon-purple to-neon-blue opacity-0 group-hover:opacity-30 animate-pulse-soft blur-sm -z-10" />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-black/80 backdrop-blur-md border border-white/10 shadow-neon-purple/20 max-h-[calc(100vh-80px)] overflow-y-auto">
        <DropdownMenuLabel className="text-white">
          {fullName || "User Account"}
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-white/10" />
        
        {/* Mobile-only menu items */}
        <div className="md:hidden">
          <DropdownMenuItem onClick={() => {}} className="text-gray-300 hover:text-neon-purple focus:text-neon-purple focus:bg-white/10">
            <Bell className="mr-2 h-4 w-4" />
            <span>Notifications</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => {}} className="text-gray-300 hover:text-neon-blue focus:text-neon-blue focus:bg-white/10">
            <Search className="mr-2 h-4 w-4" />
            <span>Search</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator className="bg-white/10 md:hidden" />
        </div>
        
        <DropdownMenuItem onClick={() => navigate('/profile')} className="text-gray-300 hover:text-neon-purple focus:text-neon-purple focus:bg-white/10">
          <User className="mr-2 h-4 w-4" />
          <span>Profile</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/settings')} className="text-gray-300 hover:text-neon-blue focus:text-neon-blue focus:bg-white/10">
          <Settings className="mr-2 h-4 w-4" />
          <span>Settings</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-white/10" />
        <DropdownMenuItem onClick={onLogout} className="text-neon-pink hover:bg-neon-pink/10 focus:bg-neon-pink/10">
          <LogOut className="mr-2 h-4 w-4" />
          <span>Logout</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserMenu;
